import React, { useState, useEffect } from "react";
import {
  Button, Card, Container, Row, Col, Alert, Spinner, Badge, Modal
} from "react-bootstrap";
import { QRCodeCanvas } from "qrcode.react";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, ArrowsClockwise, Receipt, CheckCircle, 
  XCircle, Clock, Warning, House 
} from "@phosphor-icons/react";
import AppLayout from "../layouts/AppLayout";
import usePaymentStatus from "../apis/PaymentStatusApi";


const ShowQr = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location;
  
  const { getPaymentStatus, paymentData, isLoading, error } = usePaymentStatus();
  
  const [statusChecked, setStatusChecked] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [showBackModal, setShowBackModal] = useState(false);

  const orderId = state?.orderId;
  const upiString = state?.upiString;
  const amount = state?.amount;
  const loanNumber = state?.loanNumber;
  const gateway = state?.gateway;

  // ✅ BLOCK BACK BUTTON - SHOW MODAL
  useEffect(() => {
    window.history.pushState(null, null, window.location.href);
    
    const handleBack = () => {
      window.history.pushState(null, null, window.location.href);
      setShowBackModal(true);
    };

    window.addEventListener('popstate', handleBack);
    return () => window.removeEventListener('popstate', handleBack);
  }, []);

  // ✅ HANDLE MODAL ACTIONS
  const handleGoToDashboard = () => {
    setShowBackModal(false);
    navigate('/dashboard', { replace: true });
  };

  const handleStay = () => {
    setShowBackModal(false);
  };

  // ✅ VALIDATE PAGE ACCESS
  useEffect(() => {
    console.log("═══════════════════════════════════════");
    console.log("📱 [ShowQr] Page loaded");
    console.log("   Order ID:", orderId);
    console.log("   UPI String:", upiString);
    console.log("   Amount:", amount);
    console.log("═══════════════════════════════════════");

    if (!orderId || !upiString) {
      console.error("❌ Missing QR data");
      navigate("/dashboard/", { replace: true });
    }
  }, [orderId, upiString, navigate]);

  // ✅ UPDATE STATUS FROM REDUX
  useEffect(() => {
    if (paymentData?.payment_status) {
      console.log("───────────────────────────────────────");
      console.log("🔄 [ShowQr] Status update from Redux:");
      console.log("   Status:", paymentData.payment_status);
      console.log("───────────────────────────────────────");
      setCurrentStatus(paymentData.payment_status);
    }
  }, [paymentData]);

  // ✅ CHECK PAYMENT STATUS
  const handleCheckStatus = () => {
    if (!orderId) {
      console.error("❌ No order ID");
      return;
    }

    console.log("═══════════════════════════════════════");
    console.log("🔍 [ShowQr] Checking payment status");
    console.log("   Order ID:", orderId);
    console.log("═══════════════════════════════════════");

    setStatusChecked(true);

    getPaymentStatus(orderId, (resp) => {
      console.log("───────────────────────────────────────");
      console.log("📨 [ShowQr] Status response:", resp);
      console.log("───────────────────────────────────────");

      if (resp?.is_success && resp.data) {
        const status = resp.data.payment_status;
        console.log("   Status:", status);
        setCurrentStatus(status);

        // ✅ Auto-navigate if not pending
        if (status !== "pending") {
          console.log("✅ Payment completed - Navigating to receipt");
          setTimeout(() => {
            navigate("/PaymentReceipt", {
              state: {
                orderId: orderId,
                paymentData: resp.data
              },
              replace: true
            });
          }, 1500);
        }
      } else {
        console.error("❌ Failed to check status:", resp?.message);
      }
    });
  };

  // ✅ NAVIGATE TO RECEIPT
  const handleViewReceipt = () => {
    console.log("📄 [ShowQr] Navigating to receipt");
    navigate("/PaymentReceipt", {
      state: { orderId: orderId },
      replace: true
    });
  };

  if (!orderId || !upiString) {
    return null;
  }

  const isPending = currentStatus === "pending";
  const isSuccess = currentStatus === "success" || currentStatus === "completed";
  const isFailed = currentStatus === "failed" || currentStatus === "cancelled";

  return (
    <AppLayout>
      <Container className="py-6"  style={{ paddingBottom: '100px' }}>
        <Row className="justify-content-center">
          <Col xs={12} md={8} lg={6}>
            
            {/* ⚠️ WARNING BANNER */}
            <Alert variant="warning" className="mb-3 text-center">
              <Warning size={20} className="me-2" />
              <strong>Do not go back or refresh this page!</strong>
              <br />
              <small>Use buttons below to navigate after payment</small>
            </Alert>

            {/* QR CODE CARD */}
            <Card className="shadow-lg border-0 mb-3">
              <Card.Body className="p-4 text-center">
                <h4 className="mb-3">Scan QR to Pay</h4>
                
                {/* Amount */}
                <h2 className="text-primary mb-2">
                  ₹{parseFloat(amount).toLocaleString('en-IN')}
                </h2>
                
                {/* Loan Number */}
                <p className="text-muted mb-3">{loanNumber}</p>
                
                {/* Gateway Badge */}
                <Badge bg="info" className="mb-4">
                  {gateway} UPI Payment
                </Badge>

                {/* QR CODE */}
                <div className="d-flex justify-content-center mb-4">
                  <div className="p-3 bg-white border rounded shadow-sm">
                    <QRCodeCanvas
                      value={upiString}
                      size={280}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                </div>

                <p className="small text-muted mb-4">
                  Scan with any UPI app<br />
                  (PhonePe, Google Pay, Paytm, etc.)
                </p>

                {/* STATUS DISPLAY */}
                {statusChecked && currentStatus && (
                  <Alert
                    variant={
                      isSuccess ? "success" :
                      isPending ? "warning" :
                      isFailed ? "danger" :
                      "info"
                    }
                    className="mb-3"
                  >
                    {isSuccess && <CheckCircle size={24} className="me-2" />}
                    {isPending && <Clock size={24} className="me-2" />}
                    {isFailed && <XCircle size={24} className="me-2" />}
                    
                    <strong>
                      {isSuccess && "Payment Successful!"}
                      {isPending && "Payment Pending"}
                      {isFailed && "Payment Failed"}
                    </strong>
                    
                    {isSuccess && (
                      <div className="small mt-1">Redirecting to receipt...</div>
                    )}
                  </Alert>
                )}

                {/* LOADING INDICATOR */}
                {isLoading && (
                  <Alert variant="info" className="mb-3">
                    <Spinner size="sm" className="me-2" />
                    Checking payment status...
                  </Alert>
                )}

                {/* ERROR */}
                {error && (
                  <Alert variant="danger" className="mb-3">
                    <Warning size={20} className="me-2" />
                    {error}
                  </Alert>
                )}
              </Card.Body>
            </Card>

            {/* ACTION BUTTONS */}
            <div className="d-grid gap-2">
              {/* CHECK STATUS BUTTON */}
              <Button
                variant="success"
                size="lg"
                onClick={handleCheckStatus}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Checking...
                  </>
                ) : (
                  <>
                    <ArrowsClockwise size={20} className="me-2" weight="bold" />
                    Check Payment Status
                  </>
                )}
              </Button>

              {/* VIEW RECEIPT BUTTON */}
              <Button
                variant="primary"
                size="lg"
                onClick={handleViewReceipt}
              >
                <Receipt size={20} className="me-2" weight="bold" />
                View Receipt
              </Button>

              {/* BACK TO DASHBOARD */}
              <Button
                variant="outline-secondary"
                onClick={() => navigate("/history/", { replace: true })}
              >
                <ArrowLeft size={16} className="me-2" />
                View Transactions
              </Button>
            </div>

            {/* INFO */}
            <div className="text-center mt-4 text-muted small">
              <p className="mb-1">
                Order ID: <strong>{orderId}</strong>
              </p>
              <p className="mb-10">
                🔄 Click "Check Status" after payment
                             </p>
            </div>

          </Col>
        </Row>
      </Container>

      {/* ✅ BACK BUTTON WARNING MODAL */}
      <Modal show={showBackModal} onHide={handleStay} centered backdrop="static">
        <Modal.Body className="text-center p-4">
          <Warning size={48} className="text-warning mb-3" />
          <h5 className="mb-3">Cannot Go Back!</h5>
          <p className="text-muted mb-4">
            Payment QR is active. Going back is not allowed.
            <br />
            Please use the buttons below.
          </p>
          <div className="d-grid gap-2">
            <Button 
              variant="primary" 
              size="lg"
              onClick={handleGoToDashboard}
            >
              <House size={20} className="me-2" />
              Home
            </Button>
            <Button 
              variant="outline-secondary"
              onClick={handleStay}
            >
              Stay
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </AppLayout>
  );
};

export default ShowQr;
